import { useCallback, useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { Group } from '../../primitives/Group.js';
import { Text } from '../../primitives/Text.js';
import { ImageLightbox } from '../../primitives/ImageLightbox.js';
import styles from './ImageAttachmentPicker.module.css';
import { Pressable } from '../../primitives/Pressable.js';

export type ImageAttachmentView = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  signedUrl: string;
};

type Props = {
  attachments: ImageAttachmentView[];
  compact?: boolean;
  /** Thumbnails inside a message composer (above text). */
  embedded?: boolean;
  onRemove?: (index: number) => void;
};

export function ImageAttachmentGrid({
  attachments,
  compact = false,
  embedded = false,
  onRemove,
}: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = useCallback(() => setActiveIndex(null), []);
  const active = activeIndex != null ? attachments[activeIndex] : null;

  const step = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        if (current == null || attachments.length === 0) return current;
        return (current + delta + attachments.length) % attachments.length;
      });
    },
    [attachments.length],
  );

  useEffect(() => {
    if (active == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        step(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        step(-1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, step]);

  const removeAt = useCallback(
    (index: number) => {
      onRemove?.(index);
      setActiveIndex((current) => {
        if (current == null) return current;
        if (current === index) return null;
        if (current > index) return current - 1;
        return current;
      });
    },
    [onRemove],
  );

  if (!attachments.length) return null;

  return (
    <>
      <Group
        gap="xs"
        wrap="wrap"
        className={embedded ? styles.embedded : compact ? styles.compact : undefined}
      >
        {attachments.map((a, index) => (
          <div
            key={a.id}
            className={styles.thumbWrap}
            style={embedded ? undefined : { maxWidth: compact ? 128 : 140 }}
          >
            <Pressable
              type="button"
              className={styles.thumbButton}
              onClick={() => setActiveIndex(index)}
              aria-label={`Agrandir ${a.fileName}`}
            >
              <img src={a.signedUrl} alt={a.fileName} className={styles.thumbImg} />
              {!compact && !embedded ? (
                <Text size="xs" c="secondary" className={styles.thumbName}>
                  {a.fileName}
                </Text>
              ) : null}
            </Pressable>
            {onRemove ? (
              <Pressable
                type="button"
                className={styles.thumbRemove}
                onClick={() => removeAt(index)}
                aria-label={`Retirer ${a.fileName}`}
              >
                <IconX size={14} stroke={2.2} aria-hidden />
              </Pressable>
            ) : null}
          </div>
        ))}
      </Group>

      <ImageLightbox
        open={Boolean(active)}
        onClose={close}
        src={active?.signedUrl ?? ''}
        label={active?.fileName}
        caption={active?.fileName}
        alt={active?.fileName}
      />
    </>
  );
}
